import { Component } from '@theme/component';

const DOUBLE_TAP_DELAY = 300;

/**
 * Roberts Jewelers — in-place image magnifier (ported from sterling-forever's
 * magnify.js). Zooms the image in place over a lens overlay. IN-PLACE zoom, NOT
 * a modal/dialog.
 *
 * Activation:
 *   - Desktop (mouse / fine pointer): a single click toggles the zoom, and the
 *     cursor pans by following the pointer (absolute mapping — there is no
 *     "lift the finger" on a mouse).
 *   - Mobile (touch / coarse pointer): a DOUBLE-TAP toggles the zoom, and you
 *     pan by DRAGGING. The pan is RELATIVE and accumulates, so when you reach
 *     the edge, lift, and grab again, it continues from where it was instead of
 *     snapping back to the new finger position.
 *
 * Markup:
 *   <roberts-magnify class="rj-magnify" data-zoom="2.5"
 *     data-zoom-src="<high-res url>" on:click="/toggle">
 *     <img ref="image" ...>
 *   </roberts-magnify>
 *
 * @typedef {object} Refs
 * @property {HTMLImageElement} image
 * @extends {Component<Refs>}
 */
class RobertsMagnify extends Component {
  /** @type {HTMLElement | null} */
  #lens = null;
  /** Timestamp of the previous tap, for touch double-tap detection. */
  #lastTap = 0;

  // Touch drag-pan state (pixels). #pos is the current background offset;
  // #range is how far the background can travel in each axis.
  #rangeX = 0;
  #rangeY = 0;
  #posX = 0;
  #posY = 0;
  #dragging = false;
  #dragX = 0;
  #dragY = 0;
  #dragPosX = 0;
  #dragPosY = 0;

  get #zoom() {
    return parseFloat(this.dataset.zoom ?? '') || 2.5;
  }

  /** True on touch devices (no hover / coarse pointer). */
  get #isTouch() {
    return !window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.hide();
  }

  /** @param {MouseEvent} event */
  toggle(event) {
    if (!this.#isTouch) {
      if (this.#lens) this.hide();
      else this.show(event);
      return;
    }

    // Touch: require a double-tap (two taps within DOUBLE_TAP_DELAY ms).
    const isDoubleTap = event.timeStamp - this.#lastTap < DOUBLE_TAP_DELAY;
    this.#lastTap = isDoubleTap ? 0 : event.timeStamp;
    if (!isDoubleTap) return;

    if (this.#lens) this.hide();
    else this.show(event);
  }

  /** @param {MouseEvent} [event] */
  show(event) {
    if (this.#lens) return;
    const image = this.refs.image instanceof HTMLImageElement ? this.refs.image : this.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return;
    const src = this.dataset.zoomSrc || image.currentSrc || image.src;
    if (!src) return;

    const lens = document.createElement('div');
    lens.className = 'rj-magnify__lens';
    lens.setAttribute('aria-hidden', 'true');
    lens.style.backgroundImage = `url('${src}')`;
    this.appendChild(lens);
    this.#lens = lens;
    this.setAttribute('data-zoomed', '');

    if (this.#isTouch) {
      this.#initDragPan(image);
      this.addEventListener('pointerdown', this.#dragStart);
      this.addEventListener('pointermove', this.#dragMove);
      this.addEventListener('pointerup', this.#dragEnd);
      this.addEventListener('pointercancel', this.#dragEnd);
    } else {
      this.addEventListener('pointermove', this.#hoverMove);
      if (event && typeof event.clientX === 'number') this.#hoverMove(event);
    }
  }

  hide() {
    this.removeEventListener('pointermove', this.#hoverMove);
    this.removeEventListener('pointerdown', this.#dragStart);
    this.removeEventListener('pointermove', this.#dragMove);
    this.removeEventListener('pointerup', this.#dragEnd);
    this.removeEventListener('pointercancel', this.#dragEnd);
    this.#dragging = false;
    this.#lens?.remove();
    this.#lens = null;
    this.removeAttribute('data-zoomed');
  }

  /* ----------------------------- Touch: drag-pan ----------------------------- */

  /** @param {HTMLImageElement} image */
  #initDragPan(image) {
    if (!this.#lens) return;
    const rect = this.getBoundingClientRect();
    const bgWidth = rect.width * this.#zoom;
    const aspect =
      image.naturalWidth && image.naturalHeight
        ? image.naturalHeight / image.naturalWidth
        : rect.height / rect.width;
    const bgHeight = bgWidth * aspect;

    this.#lens.style.backgroundSize = `${bgWidth}px`;
    this.#rangeX = Math.max(0, bgWidth - rect.width);
    this.#rangeY = Math.max(0, bgHeight - rect.height);
    // Start centred on the image.
    this.#posX = -this.#rangeX / 2;
    this.#posY = -this.#rangeY / 2;
    this.#applyPos();
  }

  #applyPos() {
    if (this.#lens) this.#lens.style.backgroundPosition = `${this.#posX}px ${this.#posY}px`;
  }

  /** @param {PointerEvent} event */
  #dragStart = (event) => {
    this.#dragging = true;
    this.#dragX = event.clientX;
    this.#dragY = event.clientY;
    this.#dragPosX = this.#posX;
    this.#dragPosY = this.#posY;
  };

  /** @param {PointerEvent} event */
  #dragMove = (event) => {
    if (!this.#dragging || !this.#lens) return;
    // Accumulate from where this drag started; clamp to the pannable range so
    // hitting an edge just stops (no overshoot, no snap-back).
    const nextX = this.#dragPosX + (event.clientX - this.#dragX);
    const nextY = this.#dragPosY + (event.clientY - this.#dragY);
    this.#posX = Math.min(0, Math.max(-this.#rangeX, nextX));
    this.#posY = Math.min(0, Math.max(-this.#rangeY, nextY));
    this.#applyPos();
  };

  #dragEnd = () => {
    this.#dragging = false;
  };

  /* ----------------------------- Desktop: hover ------------------------------ */

  /** @param {PointerEvent} event */
  #hoverMove = (event) => {
    if (!this.#lens) return;
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const clamp = (value) => Math.min(100, Math.max(0, value));
    const x = clamp(((event.clientX - rect.left) / rect.width) * 100);
    const y = clamp(((event.clientY - rect.top) / rect.height) * 100);

    this.#lens.style.backgroundSize = `${rect.width * this.#zoom}px`;
    this.#lens.style.backgroundPosition = `${x}% ${y}%`;
  };
}

if (!customElements.get('roberts-magnify')) {
  customElements.define('roberts-magnify', RobertsMagnify);
}
